import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable } from "rxjs";
import { HttpClientService } from "./httpclient";
import { environment } from "../../../environments/environment";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { throwError } from "rxjs/internal/observable/throwError";
import { Coordinates, RoutePointInformation } from "../models/map/required-route-information";
import { OrderLine } from "../../pb-screen/models/order-line/orderLine.interface";
import { MapConfiguration } from "../models/map/map-configuration";
import { ServerSetting } from "../models/map/server-setting";
import { ModalManagerService } from "./modal-manager.service";

@Injectable()
export class MapService {

  // Business Central composes its own routing calls as
  // <PTV Azure Functions API URL>/api/<function>?code=<function key>. We mirror that so the
  // tenant only has to change the base URL in the Go4TMS General Setup.
  private static readonly ROUTING_FUNCTION_NAME = "CalculateDistanceForMap";

  // The function key can be set as a row in the TMS xServer Settings list, using any of these
  // names (first match wins), which is how the NAV2016 wrapper reads it. That way a rotated
  // key is a setup change instead of a new PT4 deployment. Deliberately no fallback to the
  // Cloud Password: against the Go4Routing API that field holds the PTV Developer key, and
  // sending it as ?code= is rejected with 401.
  private static readonly ROUTING_FUNCTION_KEY_SETTINGS = ["Code", "Key", "FunctionKey", "Go4Routing.FunctionKey"];

  // Used when no setting row is present: the same value codeunit "Go4PTV Azure Functions"
  // carries as a locked Label.
  private static readonly DEFAULT_ROUTING_FUNCTION_KEY = "naRfm0c4DmW9OTaa5sFl";

  private isMapVisible: boolean = false;



  constructor(
    private httpClient: HttpClientService,
    private modalManager: ModalManagerService
  ) {
  }

  fetchMapConfigurationFromBusinessCentral(): Observable<MapConfiguration> {
    return this.httpClient.get(environment.tmsApiEndpointAddress + "map/settings")
      .pipe(
        map((serverSettingsInsideData: any) => serverSettingsInsideData.data as MapConfiguration),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  fetchCoordinatesForEntireRoute(requiredRoutePointInformation: RoutePointInformation[]): Observable<Coordinates[][]> {
    return this.fetchMapConfigurationFromBusinessCentral()
      .pipe(
        take(1),
        switchMap((mapConfiguration: MapConfiguration) => {
          const obs: Observable<Coordinates[]>[] = [];

          for (let i = 0; i < requiredRoutePointInformation.length - 1; i++) {
            const routePoint1 = requiredRoutePointInformation[i];
            const routePoint2 = requiredRoutePointInformation[i + 1];

            obs.push(this.fetchRoutePolylineCoords(mapConfiguration, routePoint1, routePoint2));
          }

          // Single OL
          if (requiredRoutePointInformation.length === 1) {
            obs.push(this.fetchRoutePolylineCoords(mapConfiguration, requiredRoutePointInformation[0], requiredRoutePointInformation[0]));
          }

          return forkJoin(obs);
        }),
        catchError(error => {
          return throwError(error);
        })
      );
  }

  private resolveRoutingFunctionKey(settings: ServerSetting[]): string {
    for (const settingName of MapService.ROUTING_FUNCTION_KEY_SETTINGS) {
      const setting = (settings || []).find(
        s => (s?.name || "").trim().toLowerCase() === settingName.toLowerCase()
      );
      const key = (setting?.value || "").trim();

      if (key) {
        return key;
      }
    }

    return MapService.DEFAULT_ROUTING_FUNCTION_KEY;
  }

  private buildRoutingFunctionUrl(mapConfiguration: MapConfiguration): string {
    const baseUrl = (mapConfiguration.apiUrl || "").trim().replace(/\/+$/, "");

    if (!baseUrl) {
      throw new Error(
        "No routing API is configured. Set 'PTV Azure Functions API URL' on the Go4TMS General Setup in Business Central."
      );
    }

    const functionKey = this.resolveRoutingFunctionKey(mapConfiguration.settings);

    return `${baseUrl}/api/${MapService.ROUTING_FUNCTION_NAME}?code=${encodeURIComponent(functionKey)}`;
  }

  private fetchRoutePolylineCoords(mapConfiguration: MapConfiguration, routePoint1: RoutePointInformation, routePoint2: RoutePointInformation): Observable<Coordinates[]> {
    return this.httpClient.post(
      this.buildRoutingFunctionUrl(mapConfiguration),
      {
        Latitude1: routePoint1.coordinates.y,
        Longitude1: routePoint1.coordinates.x,
        Latitude2: routePoint2.coordinates.y,
        Longitude2: routePoint2.coordinates.x,
        Config: {
          Settings: mapConfiguration.settings.map(setting => setting.name),
          Values: mapConfiguration.settings.map(setting => setting.value),
          UserName: mapConfiguration.credentials.username,
          Password: mapConfiguration.credentials.password
        }
      }
    ).pipe(
      map(data => {
        // The legacy Azure Function answered in camelCase, the Go4Routing API answers in
        // PascalCase. Read both so the map works whichever URL the tenant points at.
        const plainPoints = data["plainPoints"] ?? data["PlainPoints"];

        // Same Address
        if (!plainPoints) {
          return [
            this.GeoMinSec_2_GeoDecimal(routePoint1.coordinates.x, routePoint1.coordinates.y)];       }
        const arr: Coordinates[] = [];

        for (const coords of plainPoints) {
          arr.push(this.GeoMinSec_2_GeoDecimal(coords.x ?? coords.X, coords.y ?? coords.Y));
        }

        return arr;
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  GeoMinSec_2_GeoDecimal(xIn: number, yIn: number): Coordinates {
    let xOut: number = 0;
    let yOut: number = 0;
    let xArg: number = xIn;
    let yArg: number = yIn;

    {
      let deg: number, min: number, sec: number, tmp: number;
      let vz: number;
      if (xArg >= 0) {
        vz = 1;
      } else {
        vz = -1;
        xArg = -xArg;
      }
      deg = Math.floor(xArg / 100000);
      min = ((xArg / 100000) - deg) * 100;
      tmp = Math.floor(min);
      sec = (min - tmp) * 100;
      min = tmp;
      xArg = (deg * 100000 + (min * 60 + sec) / 0.036) * vz;
    }
    {
      let deg: number, min: number, sec: number, tmp: number;
      let vz: number;
      if (yArg >= 0) {
        vz = 1;
      } else {
        vz = -1;
        yArg = -yArg;
      }
      deg = Math.floor(yArg / 100000);
      min = ((yArg / 100000) - deg) * 100;
      tmp = Math.floor(min);
      sec = (min - tmp) * 100;
      min = tmp;
      yArg = (deg * 100000 + (min * 60 + sec) / 0.036) * vz;
    }
    xOut = xArg /100000;
    yOut = yArg/100000;

    const result: Coordinates = { x: xOut, y: yOut };
    return result;
  }

  openMapForPb(pbId: number) {
    this.modalManager.openNewModalTab(environment.redirectUrl + `/map?os=pb&ids=${pbId}`, 'MapWindow', 900, 600);
  }
  openMapForTO(toId: string) {
    this.modalManager.openNewModalTab(environment.redirectUrl + `/map?os=to&ids=${toId}`, 'MapWindow', 900, 600);
  }
}
